import cx from 'classnames'
import { useMemo } from 'react'
import LineGraph from '~/components/LineGraph'
import ReadOnlyField from '~/components/ReadOnlyField'
import CultureImage from '~/pages/slasDemo/CommandCenter/CulturePage/CultureImage'
import resolveSlasDemoAssetsPath from '~/pages/slasDemo/utils/pathResolver'
import { displayCount } from '~/utils/string'
import { type ClePlate, getClePlateType } from './ClePlate.interface'
import cs from './cle_culture_popover.scss'

const CONFLUENCY_GRAPH_DATA = [
  { day: 0, value: 22.1 },
  { day: 1, value: 29.6 },
  { day: 2, value: 49.4 },
  { day: 3, value: 78.4 },
  { day: 4, value: 93.7 },
  { day: 5, value: 98.2 },
]

function CleCultureGrowthChart({
  data,
  className,
}: {
  data: {
    [name: string]: {
      day: number
      value: number
    }[]
  }
  className?: string
}) {
  return (
    <LineGraph
      className={className}
      layoutOptions={{
        marginTop: 3,
        marginBottom: 3,
        marginLeft: 3,
        marginRight: 3,
      }}
      axisOptions={{
        showYAxis: false,
        showXAxis: false,
      }}
      options={{
        xKey: 'day',
        yKey: 'value',
        lineColor: '#2CB1BC',
        dotSize: 2,
        xDomainMax: 5,
        yDomainMax: 100,
      }}
      data={data}
    />
  )
}

export function CleCulturePopover({
  clePlate,
  wellName,
  dayToSimulate,
  actualConfluency,
  useLocalAssets,
}: {
  clePlate: ClePlate
  wellName: string
  dayToSimulate: number
  actualConfluency: number
  useLocalAssets: boolean
}) {
  const imageData = useMemo(
    () => {
      return [
        '726__20221025_145714_Bright%20Field_A3_1_001_thumbnail.png',
        '726__20221026_133235_Bright%20Field_A3_1_001_thumbnail.png',
        '726__20221027_132839_Bright%20Field_A3_1_001_thumbnail.png',
        '726__20221028_122614_Bright%20Field_A3_1_001_thumbnail.png',
        '726__20221029_152421_Bright%20Field_A3_1_001_thumbnail.png',
        '726__20221030_134054_Bright%20Field_A3_1_001_thumbnail.png',
      ].map(path => {
        return resolveSlasDemoAssetsPath(`${path}`, useLocalAssets)
      })
    },
    // Note: This used to be dependent on the AppContext. Unclear if that's still required.
    [],
  )

  return (
    <div className={cs.cleCulturePopover}>
      <div className={cs.popoverHeader}>
        <div className={cs.popoverHeaderLeft}>
          <div className={cs.popoverHeaderLabel}>
            {clePlate.cellLine.name} &middot; {getClePlateType(clePlate)}
          </div>
          <div className={cs.popoverHeaderCulture}>Well {wellName}</div>
          <div className={cs.popoverHeaderMetadata}>
            Seeded{' '}
            {dayToSimulate === 0 ? '2 hours' : displayCount('day', dayToSimulate)} ago
          </div>
        </div>
        <div className={cs.popoverHeaderRight}>
          <CleCultureGrowthChart
            data={{
              [clePlate.cellLine.name]: CONFLUENCY_GRAPH_DATA.slice(
                0,
                dayToSimulate + 1,
              ),
            }}
            className={cs.popoverChart}
          />
        </div>
      </div>
      <div className={cs.popoverData}>
        <div className={cs.popoverDataTitle}>
          <span className={cs.confluency}>{actualConfluency}% Confluent</span>{' '}
          <span className={cs.capturedAt}>1 hour ago</span>
        </div>
        <div className={cs.thumbnailContainer}>
          <CultureImage
            className={cs.popoverImage}
            thumbnail={imageData[dayToSimulate]}
          />
        </div>
      </div>
      <div className={cs.popoverMetadata}>
        <ReadOnlyField
          label='Parent Cell Line'
          value={clePlate.cellLine.parentCellLine}
          className={cx(cs.popoverMetadataField, cs.popoverMetadataHighlight)}
          condensed
        />
        <ReadOnlyField
          label='Desired Edit'
          value={clePlate.cellLine.desiredEdit}
          className={cs.popoverMetadataField}
          condensed
        />
        <ReadOnlyField
          label='Owner'
          value={clePlate.owner}
          className={cx(cs.popoverMetadataField, cs.popoverMetadataHighlight)}
          condensed
        />
        <ReadOnlyField
          label='Next Action'
          value='Media Exchange'
          className={cs.popoverMetadataField}
          condensed
        />
      </div>
    </div>
  )
}
